/** @format */
import { Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import { dateUtils } from 'common-typescript/constants';
import { CurriculumPeriod, LocalDateString, StudyRight } from 'common-typescript/types';
import moment from 'moment/moment';
import { Observable, of } from 'rxjs';
import { combineLatestWith, map } from 'rxjs/operators';
import { CurriculumPeriodEntityService, StudyYearsEntityService } from 'sis-components';
import { ParsedPlannedPeriod, ParsedPlannedPeriodService } from './parsed-planned-period.service';

@Injectable()
export class TimelineStartDateService {
    constructor(
        private readonly _curriculumPeriodEntityService: CurriculumPeriodEntityService,
        private readonly _studyYearsEntityService: StudyYearsEntityService,
        private readonly _parsedPlannedPeriodService: ParsedPlannedPeriodService,
    ) {}

    /**
     * Returns the first possibly interesting timeline date (curriculum period, study right, attainment, timing),
     * or the start date of the current year by default.
     */
    getTimelineStartDate(validatablePlan: ValidatablePlan, studyRight: StudyRight | null): Observable<moment.Moment> {
        const altMoment = dateUtils.createMoment(studyRight?.alternativeStudyStartDate);
        const validMoment = dateUtils.createMoment(studyRight?.valid?.startDate);
        const studyRightStartDate: moment.Moment | null =
            altMoment && validMoment ? moment.min(altMoment, validMoment) : (altMoment ?? validMoment);

        const curriculumPeriod$: Observable<CurriculumPeriod | null> = validatablePlan.plan.curriculumPeriodId
            ? this._curriculumPeriodEntityService.getById(validatablePlan.plan.curriculumPeriodId)
            : of(null);

        const sortedParsedPlannedPeriods = this._parsedPlannedPeriodService.getSortedParsedPlannedPeriods(validatablePlan);

        return curriculumPeriod$.pipe(
            combineLatestWith(this.getFirstPlannedPeriodStartDate(sortedParsedPlannedPeriods)),
            map(([curriculumPeriod, firstPlannedPeriodStartDate]) => {
                const curriculumPeriodStartDate: moment.Moment | null = dateUtils.createMoment(curriculumPeriod?.activePeriod?.startDate);
                const firstAttainmentDate: moment.Moment | null = this.getFirstAttainmentDate(validatablePlan);
                const sortedDates = [firstPlannedPeriodStartDate, curriculumPeriodStartDate, studyRightStartDate, firstAttainmentDate]
                    .filter(date => date)
                    .sort((a, b) => a.diff(b));
                return sortedDates[0] ?? getDefaultStartDate();
            }),
        );
    }

    private getFirstPlannedPeriodStartDate(sortedParsedPlannedPeriods: readonly ParsedPlannedPeriod[]): Observable<moment.Moment | null> {
        if (!sortedParsedPlannedPeriods.length) {
            return of(null);
        }
        const firstPeriod: ParsedPlannedPeriod = sortedParsedPlannedPeriods[0];
        return this._studyYearsEntityService
            .findStudyPeriodWithLocator(firstPeriod.parsedLocator.organisationId, firstPeriod.parsedLocator.year, firstPeriod.locator)
            .pipe(map(period => dateUtils.createMoment(period?.valid?.startDate)));
    }

    private getFirstAttainmentDate(validatablePlan: ValidatablePlan): moment.Moment | null {
        const attainmentDates: LocalDateString[] = validatablePlan
            .getAllCourseUnitAndCustomCourseUnitAttainmentsInPlan()
            .filter(attainment => attainment)
            .map(attainment => attainment.attainmentDate)
            .sort((a, b) => a.localeCompare(b));

        return attainmentDates.length ? dateUtils.createMoment(attainmentDates[0]) : null;
    }
}

function getDefaultStartDate(): moment.Moment {
    return moment().startOf('year');
}
